/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useServiceContext } from '../../root'
import { useModal } from '../../contexts/ModalContext'
import { useEsignature } from '../../hooks/useEsignature'
import RcmEsignatureModal from '../esignature/RcmEsignatureModal'
import Modal from '../Modal'
import Utils from '../../lib/Utils'

interface WorkflowCancelModalProps {
  workflowIds: number[]
  workflowNo?: string
  onClose?: () => void
  onSuccess?: () => void
  isEosFlow?: boolean
}

/**
 * Workflow Cancel Modal Component
 *
 * Allows users to cancel one or multiple workflows with an optional comment.
 * Supports e-signature validation for certain authentication types.
 *
 * Features:
 * - Single or multiple workflow cancellation
 * - Optional cancellation comment (max 255 characters)
 * - E-signature validation support
 * - Character counter for comment field
 *
 * Based on lego/page/workflows/WorkflowCancelModal.jsx
 */
export const WorkflowCancelModal: React.FC<WorkflowCancelModalProps> = ({
  workflowIds,
  workflowNo,
  onClose,
  onSuccess,
  isEosFlow = false
}) => {
  const { t } = useTranslation()
  const { api, service } = useServiceContext()
  const { hideModal } = useModal()
  const [comment, setComment] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { signAction, showDsoModal, esignMessage, handleDsoSubmit, handleDsoClose } = useEsignature()

  const maxCommentLength = 255

  useEffect(() => {
    // Add EOS-specific classes if needed
    if (isEosFlow) {
      setTimeout(() => {
        // Find the modal element and add classes to its parent dialog
        const modalElement = document.querySelector('[data-workflow-cancel-modal]')
        if (modalElement?.parentElement) {
          modalElement.parentElement.classList.add('theme-sandman', 'eos-workflows')
        }
      }, 100)
    }

    return () => {
      if (isEosFlow) {
        const modalElement = document.querySelector('[data-workflow-cancel-modal]')
        if (modalElement?.parentElement) {
          modalElement.parentElement.classList.remove('theme-sandman', 'eos-workflows')
        }
      }
    }
  }, [isEosFlow])

  const handleClose = () => {
    if (onClose) {
      onClose()
      return
    }
    hideModal()
  }

  /**
   * Handle workflow cancellation
   * This will be called after e-signature validation (if required)
   */
  const handleCancelWorkflows = async (validation?: any) => {
    try {
      setIsSubmitting(true)

      await api.workflows.cancelWorkflowsWithComments(workflowIds, comment, validation)

      // Show success toast
      service.toast.success(t('WORKFLOW_CANCELLATION_TOAST_MESSAGE'))

      // Call success callback
      if (onSuccess) {
        onSuccess()
      }

      handleClose()
    } catch (error: any) {
      // Show error toast
      const errorMessage = error?.data?.message || error?.message || t('ERROR_OCCURRED')
      service.toast.error(errorMessage)
      handleClose()
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSubmit = async () => {
    setIsSubmitting(true)
    try {
      const validation = await signAction('ADMIN_ACTION', 'ADMIN_ACTION_CANCEL', {
        keepParentModalsOpen: true
      })
      await handleCancelWorkflows(validation)
    } catch {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal
      show={true}
      handleClose={handleClose}
      title={t('CANCEL_WORKFLOWS_MODAL')}
      size="md"
      className="workflow-cancel-modal"
      dataTestId="workflow-cancel-modal"
      id="workflow-cancel-modal"
    >
      <div data-workflow-cancel-modal>
        <Modal.Body>
          {/* Workflow ID label (only for multiple workflows) */}
          {workflowIds.length > 1 && (
            <label className="control-label mb0">{t('CANCEL_WORKFLOW_MODAL_WORKFLOW_ID')}</label>
          )}

          {/* Workflow info */}
          <p className="whitespaceprewrap">
            {workflowIds.length > 1 && <span>{t('MULTIPLE_WORKFLOWS')}</span>}
            {workflowIds.length === 1 && (
              <>
                <span>{t('CANCELLATION_COMMENT_MODAL_WF_CANCEL_QUESTION')}</span>
                <b> {workflowNo} </b>
              </>
            )}
          </p>

          {/* Comment textarea */}
          <div style={{ width: '100%', maxWidth: '285px' }}>
            <label className="control-label mb0">
              <strong>{t('CANCEL_WORKFLOW_COMMENT')}</strong>
            </label>
            <textarea
              className="form-control"
              maxLength={maxCommentLength}
              name="cancellationComment"
              onChange={(e) => setComment(e.target.value)}
              value={comment}
              rows={6}
              id="workflow-cancel-comment-textarea"
            />
            <div className="help-block" style={{ textAlign: 'right', marginTop: '5px' }}>
              {Utils.formatTextAreaCounter(comment.length, maxCommentLength, t('CHAR_COUNT_LIMIT'))}
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <button
            data-action="cancel"
            className="btn btn-link"
            onClick={handleClose}
            disabled={isSubmitting}
            id="workflow-cancel-modal-cancel-btn"
          >
            {t('CANCEL')}
          </button>
          <button
            data-action="ok"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
            id="workflow-cancel-modal-submit-btn"
          >
            {t('CANCEL_WORKFLOW_MODAL_BUTTON')}
          </button>
        </Modal.Footer>
      </div>
      {showDsoModal && (
        <RcmEsignatureModal onSubmit={handleDsoSubmit} onClose={handleDsoClose} esignMessage={esignMessage} />
      )}
    </Modal>
  )
}

export default WorkflowCancelModal
